<template>
    <div class="mc-liveview__map" :class="{ 'detail-panel-open': selectedDetail }">
        <div class="mc-map-wrapper">
            <!-- Legend area -->
            <div class="mc-map-legend">
                <div class="mc-map-legend__item">
                    <span class="legend-marker west-marker"></span>
                    <span>West (Jakarta)</span>
                </div>
                <div class="mc-map-legend__item">
                    <span class="legend-marker east-marker"></span>
                    <span>East (Surabaya)</span>
                </div>
            </div>

            <l-map ref="UserMap" :center="mapCenter" :zoom="mapZoom" class="mc-map" style="height: 100%;"
                @ready="onMapReady">
                <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <v-marker-cluster v-if="clusterEnabled">
                    <l-marker v-for="tech in validUsersFiltered" :key="tech.user_id" :lat-lng="getLatLng(tech)"
                        :icon="getCustomIcon(tech)" :ref="`marker-${tech.user_id}`" @click="onMarkerClick(tech)" />
                </v-marker-cluster>
                <template v-if="!clusterEnabled">
                    <l-marker v-for="tech in validUsersFiltered" :key="tech.user_id" :lat-lng="getLatLng(tech)"
                        :icon="getCustomIcon(tech)" :ref="'marker-' + tech.user_id" @click="onMarkerClick(tech)" />
                </template>

                <l-circle v-for="tech in validUsersWithRadius" :key="`circle-${tech.user_id}`"
                    :lat-lng="getAddressLatLng(tech)" :radius="(tech.address?.radius || 0) * 1000"
                    :color="tech.area === 'West' ? '#3b82f6' : '#16a34a'"
                    :fill-color="tech.area === 'West' ? '#3b82f6' : '#16a34a'" :weight="1" :fill-opacity="0.1"
                    :opacity="0.4" />
                <l-marker v-if="customerLocation && customerLocation.lat && customerLocation.lng"
                    :lat-lng="[customerLocation.lat, customerLocation.lng]" :icon="customerIcon" :draggable="true"
                    @dragend="onCustomerDragEnd" />
            </l-map>

            <transition name="slide-panel">
                <TechnicianStockPanel v-if="selectedDetail" :detail="selectedDetail" @close="closePanel" />
            </transition>
        </div>
    </div>
</template>

<script>
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LCircle } from 'vue2-leaflet';
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import TechnicianStockPanel from './TechnicianStockPanel.vue';

const editingIcon = L.icon({
    iconUrl: 'https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const customerIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png', // atau pakai icon sendiri
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

export default {
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LCircle,
        'v-marker-cluster': Vue2LeafletMarkerCluster,
        TechnicianStockPanel
    },
    data() {
        return {
            pendingCenterEvent: null,
            customerIcon: customerIcon,
            mapObject: null,
            selectedDetail: null,
            internalUpdate: false,
            lastClickedMarker: null,
            isCentering: false,
            moveEndHandler: null,
            activeHighlightId: null,
            highlightInterval: null,
            draggablePosition: null,      // { lat, lng } untuk marker draggable
            isFlying: false,        // Flag untuk menandai sedang melakukan flyTo dari geocode
            suppressZoomSync: false,  // 🔒 Matikan sinkronisasi store sementara
            pendingCenterInterval: null,
        };
    },

    computed: {
        editingIcon() {
            return editingIcon;
        },
        sidebarOpen() { return this.$store.state.sidebar.sideBarOpen; },
        clusterEnabled() { return this.$store.state.users.userCluster; },
        technicians() { return this.$store.state.users.technicians || []; },
        validUsers() {
            return this.technicians.filter(t => {
                if (!t.address) return false;
                const lat = parseFloat(t.address.latitude);
                const lng = parseFloat(t.address.longitude);
                return !isNaN(lat) && !isNaN(lng);
            });
        },
        mapCenter() {
            // Jika sedang dalam proses flyTo, pakai center dari map object (biar stabil)
            if ((this.internalUpdate || this.suppressZoomSync) && this.mapObject) {
                const center = this.mapObject.getCenter();
                return L.latLng(center.lat, center.lng);
            }
            // Selain itu pakai dari store
            const c = this.$store.state.map.center;
            return L.latLng(c.lat || -2.5489, c.lng || 118.0149);
        },
        mapZoom() {
            if ((this.internalUpdate || this.suppressZoomSync) && this.mapObject) {
                return this.mapObject.getZoom();
            }
            return this.$store.state.map.zoom || 5;
        },
        selectedTechnicianFromStore() {
            return this.$store.state.map.selectedTechnician;
        },
        validUsersWithRadius() {
            // Ambil semua teknisi yang memiliki radius valid
            let users = this.validUsers.filter(tech => {
                if (!tech.address) return false;
                const addr = tech.address;
                const lat = parseFloat(addr.latitude);
                const lng = parseFloat(addr.longitude);
                const radius = Number(addr.radius);
                return !isNaN(lat) && !isNaN(lng) && !isNaN(radius) && radius > 0;
            });

            // Jika ada teknisi dipilih, filter hanya dia
            if (this.selectedDetail) {
                users = users.filter(t => t.user_id === this.selectedDetail.user_id);
            }
            return users;
        },
        validUsersFiltered() {
            // Jika ada teknisi dipilih, tampilkan hanya teknisi itu
            if (this.selectedDetail) {
                return this.validUsers.filter(t => t.user_id === this.selectedDetail.user_id);
            }
            // Jika tidak, tampilkan semua
            return this.validUsers;
        },
        customerLocation() {
            return this.$store.state.map.customerLocation;
        },
    },

    watch: {
        '$store.state.map.draggableMarkerPosition': {
            handler(newPos) {
                if (newPos) {
                    this.draggablePosition = { ...newPos };
                    // Jika marker sudah ada, pindahkan posisinya
                    const markerComp = this.$refs.draggableMarker;
                    if (markerComp && markerComp.mapObject) {
                        markerComp.mapObject.setLatLng([newPos.lat, newPos.lng]);
                    }
                } else {
                    this.draggablePosition = null;
                }
            },
            deep: true,
            immediate: true
        },

        selectedTechnicianFromStore(tech) {
            if (this.$store.state.map.disableAutoCenter) return;

            this.selectedDetail = tech;

            if (!tech) {
                if (this.lastClickedMarker) {
                    this.lastClickedMarker.setZIndexOffset(0);
                    this.lastClickedMarker._icon?.classList.remove('marker-highlighted');
                    this.lastClickedMarker = null;
                }
                this.activeHighlightId = null;
                this.draggablePosition = null;
                this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', null);
                return;
            }

            // Inisialisasi draggable marker
            let lat = tech.latitude ?? tech.lat;
            let lng = tech.longitude ?? tech.lng;
            if ((lat == null || isNaN(parseFloat(lat))) && tech.address) {
                lat = tech.address.latitude;
                lng = tech.address.longitude;
            }
            lat = parseFloat(lat);
            lng = parseFloat(lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                this.draggablePosition = { lat, lng };
                this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
            }

            // Jika sedang animasi, tunggu selesai lalu center
            if (this.isFlying || this.suppressZoomSync) {
                if (this.pendingCenterInterval) clearInterval(this.pendingCenterInterval);
                this.pendingCenterInterval = setInterval(() => {
                    if (!this.isFlying && !this.suppressZoomSync) {
                        clearInterval(this.pendingCenterInterval);
                        this.pendingCenterInterval = null;
                        this.centerAndHighlight(tech);
                    }
                }, 100);
                return;
            }

            this.centerAndHighlight(tech);
        },
    },

    beforeDestroy() {
        this.$root.$off('map-fly-to-pin', this.flyToPin);
        this.$root.$off('map-center-to-customer', this.centerToCustomerOnly);
        if (this.highlightInterval) clearInterval(this.highlightInterval)
        if (this.pendingCenterInterval) {
            clearInterval(this.pendingCenterInterval);
            this.pendingCenterInterval = null;
        }
    },

    mounted() {
        this.$root.$on('map-center-to-customer', (lat, lng, zoom) => {
            if (this.mapObject) {
                this.centerToCustomerOnly(lat, lng, zoom);
            } else {
                // Simpan event untuk dijalankan setelah map ready
                this.pendingCenterEvent = { lat, lng, zoom };
            }
        });
    },

    methods: {
        // Di dalam methods (sejajar dengan onMarkerDragEnd dll)
        async onCustomerDragEnd(e) {
            const { lat, lng } = e.target.getLatLng();

            // 1. Update koordinat dulu (biar marker langsung pindah, alamat lama tetap sementara)
            this.$store.commit('map/SET_CUSTOMER_LOCATION', {
                lat,
                lng,
                address: this.customerLocation?.address || '',
                name: this.customerLocation?.name || ''
            });

            // 2. Reverse geocoding untuk mendapatkan alamat baru
            try {
                const response = await fetch(
                    `${process.env.VUE_APP_MAP_URL}/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=id`
                );
                const data = await response.json();
                if (data && data.display_name) {
                    // Update store dengan alamat yang baru ditemukan
                    this.$store.commit('map/SET_CUSTOMER_LOCATION', {
                        lat,
                        lng,
                        address: data.display_name,
                        name: this.customerLocation?.name || ''
                    });
                    this.$toast?.success('Alamat diperbarui sesuai titik drag');
                } else {
                    this.$toast?.warning('Alamat tidak ditemukan untuk titik ini');
                }
            } catch (err) {
                console.error('Reverse geocoding error:', err);
                this.$toast?.error('Gagal mendapatkan alamat');
            }
        },

        centerToCustomerOnly(lat, lng, zoomLevel = 16) {
            if (!this.mapObject) return;
            this.mapObject.invalidateSize();
            this.mapObject.flyTo([lat, lng], zoomLevel, { duration: 0.6 });
            // update store setelah animasi
            setTimeout(() => {
                const center = this.mapObject.getCenter();
                this.$store.commit('map/SET_CENTER', { lat: center.lat, lng: center.lng });
                this.$store.commit('map/SET_ZOOM', this.mapObject.getZoom());
            }, 600);
        },

        resetMapToDefault() {
            // Jangan lakukan reset jika sedang ada animasi/sinkronisasi
            if (this.isFlying || this.suppressZoomSync) return;

            // Matikan flag internal
            this.internalUpdate = true;
            this.suppressZoomSync = true;

            // Zoom & center default (zoom 5.56, center Indonesia)
            const defaultCenter = [-2.5489, 118.0149];
            const defaultZoom = 5.56;

            this.$store.commit('map/SET_DISABLE_AUTO_CENTER', true);
            if (this.mapObject) {
                this.mapObject.setView(defaultCenter, defaultZoom, { animate: true, duration: 0.5 });
            }

            // Simpan ke store agar computed property mapCenter/mapZoom sinkron
            this.$store.commit('map/SET_CENTER', { lat: defaultCenter[0], lng: defaultCenter[1] });
            this.$store.commit('map/SET_ZOOM', defaultZoom);

            // Bersihkan draggable marker
            this.draggablePosition = null;
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', null);

            // Bersihkan highlight marker
            if (this.lastClickedMarker) {
                this.lastClickedMarker.setZIndexOffset(0);
                this.lastClickedMarker._icon?.classList.remove('marker-highlighted');
                this.lastClickedMarker = null;
            }
            this.activeHighlightId = null;

            // Kembalikan flag setelah animasi
            setTimeout(() => {
                this.internalUpdate = false;
                this.suppressZoomSync = false;
                this.mapObject?.invalidateSize();
                this.$store.commit('map/SET_DISABLE_AUTO_CENTER', false);
            }, 600);
        },

        onMarkerDragEnd(e) {
            const marker = e.target;
            const { lat, lng } = marker.getLatLng();
            // Update posisi pin dan store, tanpa zoom
            this.draggablePosition = { lat, lng };
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
            // Tidak perlu zoom, hanya update form
        },
        flyToPin(lat, lng, zoomLevel = 16) {
            this.draggablePosition = { lat, lng };
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
            if (!this.mapObject) return;

            // Jika ada teknisi terpilih dan customer, tampilkan keduanya
            if (this.selectedDetail && this.customerLocation && this.customerLocation.lat) {
                const techLat = this.selectedDetail.latitude ?? this.selectedDetail.lat;
                const techLng = this.selectedDetail.longitude ?? this.selectedDetail.lng;
                if (techLat && techLng) {
                    const bounds = L.latLngBounds([this.customerLocation.lat, this.customerLocation.lng], [techLat, techLng]);
                    this.mapObject.fitBounds(bounds, { padding: [50, 50], duration: 0.6 });
                    return;
                }
            }

            // Fallback: langsung ke titik
            this.isFlying = true;
            this.internalUpdate = true;
            this.suppressZoomSync = true;
            this.mapObject.setView([lat, lng], zoomLevel, { animate: true, duration: 0.6 });
            setTimeout(() => {
                this.isFlying = false;
                this.internalUpdate = false;
                this.suppressZoomSync = false;
                const center = this.mapObject.getCenter();
                this.$store.commit('map/SET_CENTER', { lat: center.lat, lng: center.lng });
                this.$store.commit('map/SET_ZOOM', this.mapObject.getZoom());
            }, 800);
        },
        onMapReady(map) {
            this.mapObject = map;
            if (this.pendingCenterEvent) {
                this.centerToCustomerOnly(
                    this.pendingCenterEvent.lat,
                    this.pendingCenterEvent.lng,
                    this.pendingCenterEvent.zoom
                );
                this.pendingCenterEvent = null;
            }
            this.$nextTick(() => {
                map.invalidateSize();
                this.startHighlightWatcher();
                if (this.selectedTechnicianFromStore) this.centerAndHighlight(this.selectedTechnicianFromStore);
            });
        },
        onMarkerClick(tech) {
            // Aktifkan kembali auto-center karena user sengaja memilih teknisi
            this.$store.commit('map/SET_DISABLE_AUTO_CENTER', false);

            // Normalisasi data teknisi (flat) seperti di SideBar
            const normalized = {
                id: tech.user_id,
                user_id: tech.user_id,
                name: tech.name,
                area: tech.address?.area || tech.area || 'West',
                address: tech.address?.address || '',
                latitude: tech.address?.latitude ?? tech.latitude ?? null,
                longitude: tech.address?.longitude ?? tech.longitude ?? null,
                radius: tech.address?.radius ?? tech.radius ?? 80,
                email: tech.email,
            };
            this.$store.commit('map/SET_SELECTED_TECHNICIAN', normalized);
            this.applyHighlight();
        },
        applyHighlight() {
            const tech = this.$store.state.map.selectedTechnician;
            if (!tech || !tech.user_id) return;
            const markerComp = this.$refs[`marker-${tech.user_id}`];
            const marker = markerComp?.[0]?.mapObject || markerComp?.mapObject;
            if (!marker || !marker._icon) return;
            if (this.lastClickedMarker && this.lastClickedMarker !== marker) {
                this.lastClickedMarker.setZIndexOffset(0);
                this.lastClickedMarker._icon?.classList.remove('marker-highlighted');
            }
            marker.setZIndexOffset(10000);
            marker._icon.classList.add('marker-highlighted');
            this.lastClickedMarker = marker;
            this.activeHighlightId = tech.user_id;
        },
        startHighlightWatcher() {
            if (this.highlightInterval) return;
            this.highlightInterval = setInterval(() => {
                if (this.selectedTechnicianFromStore && this.mapObject) this.applyHighlight();
            }, 500);
        },
        cancelPendingCentering() {
            if (this.moveEndHandler) {
                this.mapObject?.off('moveend', this.moveEndHandler);
                this.moveEndHandler = null;
            }
            this.isCentering = false;
            this.internalUpdate = false;
        },
        async centerAndHighlight(tech) {
            if (!this.mapObject) return setTimeout(() => this.centerAndHighlight(tech), 200);
            if (this.isFlying || this.suppressZoomSync) return;

            let lat, lng;
            if (tech.address) {
                lat = parseFloat(tech.address.latitude);
                lng = parseFloat(tech.address.longitude);
            }
            if (isNaN(lat) || isNaN(lng)) {
                this.resetMapToDefault();
                return;
            }

            const customer = this.customerLocation;
            this.mapObject.invalidateSize();

            if (customer && customer.lat && customer.lng) {
                const bounds = L.latLngBounds([customer.lat, customer.lng], [lat, lng]);
                this.mapObject.fitBounds(bounds, { padding: [50, 50], duration: 0.6 });
            } else {
                this.mapObject.flyTo([lat, lng], 14, { duration: 0.6 });
            }

            // Sinkronisasi store setelah animasi
            setTimeout(() => {
                const center = this.mapObject.getCenter();
                this.$store.commit('map/SET_CENTER', { lat: center.lat, lng: center.lng });
                this.$store.commit('map/SET_ZOOM', this.mapObject.getZoom());
            }, 600);

            this.applyHighlight();
        },
        closePanel() {
            this.$store.commit('map/SET_DISABLE_AUTO_CENTER', false);
            // Bersihkan marker draggable
            this.draggablePosition = null
            if (this.lastClickedMarker) {
                this.lastClickedMarker.setZIndexOffset(0)
                this.lastClickedMarker._icon?.classList.remove('marker-highlighted')
                this.lastClickedMarker = null
            }
            this.selectedDetail = null
            this.$store.commit('map/SET_SELECTED_TECHNICIAN', null)
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', null)
        },
        getLatLng(tech) {
            if (!tech.address) return [0, 0];
            const lat = parseFloat(tech.address.latitude);
            const lng = parseFloat(tech.address.longitude);
            return [lat, lng];
        },
        getAddressLatLng(tech) {
            if (!tech.address) return [0, 0];
            const lat = parseFloat(tech.address.latitude);
            const lng = parseFloat(tech.address.longitude);
            if (isNaN(lat) || isNaN(lng)) return [0, 0];
            return [lat, lng];
        },
        getCustomIcon(tech) {
            const area = tech.area || 'West';
            const iconUrl = area === 'West'
                ? require('@/assets/images/caricon/marker-west.svg')
                : require('@/assets/images/caricon/marker-east.svg');
            const labelHtml = this.$store.state.users.markerPlate
                ? `<div class="marker-label bg-white rounded px-1 shadow-sm"><strong>${tech.name}</strong></div>`
                : '';
            const html = `
          <div class="marker-wrap">
            ${labelHtml}
            <div class="marker-pin marker-pin--${area === 'West' ? 'west' : 'east'}">
              <img src="${iconUrl}" />
            </div>
          </div>`;
            return L.divIcon({ html, className: 'custom-leaflet-marker', iconSize: [36, 50], iconAnchor: [18, 50] });
        },
        toggleSidebar() { this.$store.commit('sidebar/toggleSideBar'); },
        invalidateMap() {
            if (this.mapObject) {
                this.$nextTick(() => {
                    this.mapObject.invalidateSize();
                });
            }
        },
    },
};
</script>

<style scoped>
.mc-liveview__map {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
}

.mc-map-wrapper {
    position: relative;
    height: 100%;
    width: 100%;
}

.mc-map {
    height: 100%;
    width: 100%;
    z-index: 0;
}

.mc-liveview__map,
.mc-map-wrapper,
.mc-map {
    height: 100% !important;
    /* jika perlu, override */
    width: 100% !important;
}

.mc-map-legend {
    position: absolute;
    bottom: 24px;
    left: 16px;
    z-index: 500;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    padding: 8px 12px;
    border-radius: 8px;
    display: flex;
    gap: 14px;
    font-size: 0.75rem;
}

.legend-marker {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 4px;
}

.west-marker {
    background-color: #3b82f6;
}

.east-marker {
    background-color: #16a34a;
    /* hijau */
}

.slide-panel-enter-active,
.slide-panel-leave-active {
    transition: transform 0.3s ease;
}

.slide-panel-enter,
.slide-panel-leave-to {
    transform: translateX(100%);
}

/* global marker styles */
.custom-leaflet-marker .marker-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.custom-leaflet-marker .marker-label {
    padding: 2px 6px;
    font-size: 0.6875rem;
    background: white;
    border-radius: 4px;
    margin-bottom: 2px;
}

.custom-leaflet-marker .marker-pin {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: white;
    border: 2px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.custom-leaflet-marker .marker-pin--west {
    border-color: #3b82f6;
}

.custom-leaflet-marker .marker-pin--east {
    border-color: #16a34a;
    /* hijau */
}

.custom-leaflet-marker.marker-highlighted .marker-pin {
    transform: scale(1.2);
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.3);
}

/* Style untuk draggable marker */
.draggable-marker-pin {
    display: flex;
    justify-content: center;
    align-items: center;
    filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.3));
    transition: transform 0.1s ease;
}

.draggable-marker-pin i {
    font-size: 32px;
    color: #ef4444;
}

/* Saat marker di-drag, beri efek scale */
.leaflet-marker-dragging .draggable-marker-pin {
    transform: scale(1.2);
}

.draggable-marker-fa {
    background: transparent !important;
    border: none !important;
}

.draggable-marker-fa i {
    text-shadow: 0 2px 3px rgba(0, 0, 0, 0.3);
}

.draggable-circle-marker {
    background: transparent !important;
}

.leaflet-marker-dragging .leaflet-marker-icon {
    transition: transform 0.1s ease;
    transform: scale(1.1);
    cursor: grabbing;
}
</style>
